import type { RefObject } from "react";
import styled from "styled-components";

import { ChevronIcon } from "../../icons/glyphs";
import { TooltipIconButton } from "../IconButton";
import { Text, defaultTextVariants } from "../Text";
import { CompositeListSurface } from "../foundations/compositeList";
import { yScrollbarStyles } from "../foundations/scrollbar";
import { CapturedFrameListItem } from "./CapturedFrameListItem";
import {
  CAPTURED_FRAME_LIST_ROW_HEIGHT,
  CAPTURED_FRAME_PAGINATION_MIN_HEIGHT,
} from "./MediaAnnotationStudioConstraint";
import { annotationStudioText } from "./messages";
import type {
  AnnotationFrameDraft,
  AnnotationFrameSummary,
  AnnotationSequence,
  AnnotationStream,
  AnnotationStudioLocale,
  AnnotationStudioViewMode,
  AnnotationTarget,
} from "./types";

export interface CapturedFrameListProps {
  readonly locale: AnnotationStudioLocale;
  readonly viewMode: AnnotationStudioViewMode;
  readonly sequences: readonly AnnotationSequence[];
  readonly streams: readonly AnnotationStream[];
  readonly targets: readonly AnnotationTarget[];
  readonly displayedFrameCount: number;
  readonly visibleFrames: readonly (AnnotationFrameDraft | AnnotationFrameSummary)[];
  readonly previewFrame?: AnnotationFrameDraft | AnnotationFrameSummary;
  readonly previewOutsideFilter: boolean;
  readonly filterActive: boolean;
  readonly selectedSequenceId: string | null;
  readonly activeFrameId: string | null;
  readonly selectedFrameId: string | null;
  readonly previewFrameId: string | null;
  readonly materializingFrameId?: string | null;
  readonly materializationFailedFrameId?: string | null;
  readonly savedAnnotationFrameIds: ReadonlySet<string>;
  readonly dirtyFrameIds: ReadonlySet<string>;
  readonly saveConflictFrameIds: ReadonlySet<string>;
  readonly validationIssueFrameIds: ReadonlySet<string>;
  readonly page: number;
  readonly pageCount: number;
  readonly listViewportRef: RefObject<HTMLDivElement | null>;
  readonly listRef: RefObject<HTMLUListElement | null>;
  readonly authoring: boolean;
  readonly removeAvailable: boolean;
  readonly removeDisabled: boolean;
  readonly onSelectFrame: (frameId: string) => void;
  readonly onTogglePreview: (frameId: string) => void;
  readonly onRequestRemove: (frameId: string) => void;
  readonly onPreviousPage: () => void;
  readonly onNextPage: () => void;
}

const ListViewport = styled.div`
  display: flex;
  min-width: 0;
  /* 목록은 한 행까지 줄어들 수 있고, 현재 페이지보다 큰 빈 공간은 요구하지 않는다. */
  min-height: ${CAPTURED_FRAME_LIST_ROW_HEIGHT};
  flex: 1 1 auto;
  flex-direction: column;
  ${yScrollbarStyles}
  overflow: hidden auto;

  /* 목록 surface 는 자기 행들의 높이를 유지한다. 줄어들게 두면 surface 가 자기 overflow 로 행을
     잘라내 스크롤로도 닿을 수 없는 행이 생긴다. */
  ${CompositeListSurface} {
    flex: 0 0 auto;
  }

  &[hidden] {
    display: none;
  }
`;

/** 다른 페이지로 가는 유일한 길이라 줄어드는 쪽에 서지 않는다 — 섹션의 고정 마지막 행이다. */
const Pagination = styled.div`
  display: flex;
  min-height: ${CAPTURED_FRAME_PAGINATION_MIN_HEIGHT};
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  gap: 0.35rem;
`;

const PageLabel = styled(Text)`
  flex: 1;
  color: ${({ theme }) => theme.color.text.subtle};
  font-variant-numeric: tabular-nums;
  text-align: center;
`;

const PreviousPageIcon = styled(ChevronIcon)`
  transform: rotate(90deg);
`;

const NextPageIcon = styled(ChevronIcon)`
  transform: rotate(-90deg);
`;

export function CapturedFrameList({
  locale,
  viewMode,
  sequences,
  streams,
  targets,
  displayedFrameCount,
  visibleFrames,
  previewFrame,
  previewOutsideFilter,
  filterActive,
  selectedSequenceId,
  activeFrameId,
  selectedFrameId,
  previewFrameId,
  materializingFrameId = null,
  materializationFailedFrameId = null,
  savedAnnotationFrameIds,
  dirtyFrameIds,
  saveConflictFrameIds,
  validationIssueFrameIds,
  page,
  pageCount,
  listViewportRef,
  listRef,
  authoring,
  removeAvailable,
  removeDisabled,
  onSelectFrame,
  onTogglePreview,
  onRequestRemove,
  onPreviousPage,
  onNextPage,
}: CapturedFrameListProps) {
  const renderedFrames =
    previewOutsideFilter && previewFrame
      ? [previewFrame, ...visibleFrames.filter((frame) => frame.frameId !== previewFrame.frameId)]
      : visibleFrames;

  return (
    <>
      <ListViewport
        ref={listViewportRef}
        data-captured-frame-list-viewport=""
        data-captured-frame-list-mode="frames"
        hidden={displayedFrameCount === 0 && !previewOutsideFilter}
      >
        <CompositeListSurface
          ref={listRef}
          data-captured-frame-list=""
          aria-label={annotationStudioText("capturedFrame", locale)}
        >
          {renderedFrames.map((frame) => (
            <CapturedFrameListItem
              key={frame.frameId}
              locale={locale}
              viewMode={viewMode}
              sequences={sequences}
              streams={streams}
              targets={targets}
              frame={frame}
              contextual={
                !filterActive &&
                selectedSequenceId !== null &&
                frame.sequenceId === selectedSequenceId
              }
              outsideFilter={previewOutsideFilter && frame.frameId === previewFrame?.frameId}
              active={frame.frameId === activeFrameId}
              selected={frame.frameId === selectedFrameId}
              previewed={frame.frameId === previewFrameId}
              materializing={frame.frameId === materializingFrameId}
              materializationFailed={frame.frameId === materializationFailedFrameId}
              hasSavedAnnotation={savedAnnotationFrameIds.has(frame.frameId)}
              dirty={dirtyFrameIds.has(frame.frameId)}
              saveConflict={saveConflictFrameIds.has(frame.frameId)}
              needsAttention={validationIssueFrameIds.has(frame.frameId)}
              authoring={authoring}
              removeAvailable={removeAvailable}
              removeDisabled={removeDisabled}
              onSelect={() => onSelectFrame(frame.frameId)}
              onTogglePreview={() => onTogglePreview(frame.frameId)}
              onRequestRemove={() => onRequestRemove(frame.frameId)}
            />
          ))}
        </CompositeListSurface>
      </ListViewport>
      {displayedFrameCount > 0 ? (
        <Pagination
          role="group"
          aria-label={annotationStudioText("capturedFramePagination", locale)}
        >
          <TooltipIconButton
            size="sm"
            variant="ghost"
            aria-label={annotationStudioText("previousFramePage", locale)}
            tooltip={{
              content: annotationStudioText("previousFramePage", locale),
              placement: "bottom",
            }}
            disabled={page <= 1}
            onClick={onPreviousPage}
          >
            <PreviousPageIcon />
          </TooltipIconButton>
          <PageLabel variant={defaultTextVariants["caption-normal"]}>
            {page} / {pageCount}
          </PageLabel>
          <TooltipIconButton
            size="sm"
            variant="ghost"
            aria-label={annotationStudioText("nextFramePage", locale)}
            tooltip={{
              content: annotationStudioText("nextFramePage", locale),
              placement: "bottom",
            }}
            disabled={page >= pageCount}
            onClick={onNextPage}
          >
            <NextPageIcon />
          </TooltipIconButton>
        </Pagination>
      ) : null}
    </>
  );
}
